import React, { useCallback, useEffect, useRef } from "react";

import FilterMenu from "@/components/communications-chat-components/FilterMenu";
import SearchField from "@/components/utils/SearchField";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import User from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/User";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import { Debouncer } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/Debouncer";
import { ConversationMember } from "@/models/ai-chat/ConversationMember";
import { LazoChatConversation, mapToShell } from "@/models/ai-chat/LazoChatConversation";
import { LazoChatTypingDTO } from "@/models/ai-chat/LazoChatTyping";
import LazoUserService from "@/services/LazoUserService";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import { LazoTeamOfCompany } from "@/types/LazoTeamOfCompany";
import { Box, Divider, Typography, styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import LoadingDotsAnimated from "../utils/LoadingDotsAnimated";
import { generateSingleFirstChatMessage } from "./ChatBar";
import ChatListItem from "./ChatListItem";
import { EmptyChatList } from "./empty-chat-components/EmptyChatList";
import { LazoExpertsList } from "./one/LazoExpertsList";
import { useOneChatContext } from "./one/OneChatContext";
import { LoadingIcon } from "./portal/PortalChatList";

const debouncer = new Debouncer(250);

export function ChatList({ userTyping, handleStartChat }: { userTyping: LazoChatTypingDTO; handleStartChat: () => void }) {
  const { t } = useTranslation("chat");

  const {
    chats,
    handleChatSelect: onChatSelect,
    selectedChat,
    setChatsSearchValue,
    chatsFirstLoad,
    chatsLoadingPage,
    fetchChatsPage,
    selectedActivityFilter,
    setSelectedActivityFilter,
    selectedDateFilter,
    setSelectedDateFilter,
    setChatPreview,
    setNewConversationMembers,
    createPrivateGroupChat,
    readOnly,
  } = useOneChatContext();

  const currentUserExternalId = AuthService.getUser()?.externalId;
  const tenantMembership = useTenantMemberships().currentTenantMembership;

  const lastChatListItemRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchChatsPage();
          observer.unobserve(lastChatListItemRef.current);
        }
      },
      { threshold: 0.1 }
    );

    if (lastChatListItemRef.current) {
      observer.observe(lastChatListItemRef.current);
    }

    return () => {
      if (lastChatListItemRef.current) {
        observer.unobserve(lastChatListItemRef.current);
      }
    };
  }, [chats]);

  const handleSearchType = (searchValue: string) => {
    if (searchValue) {
      const lowerCaseSearchValue = searchValue.toLowerCase();
      setChatsSearchValue(lowerCaseSearchValue);
    } else {
      setChatsSearchValue(null);
    }
  };

  const handleSearchField = (searchRef: React.RefObject<HTMLInputElement>) => {
    let searchValue = searchRef.current?.value || "";
    handleSearchType(searchValue);
  };

  const supportChat = () => {
    LazoUserService.getSupportUser().then((res) => {
      res.onSuccess((supportUser) => {
        const supUser: LazoTeamOfCompany = {
          email: supportUser.email,
          fullName: supportUser.user.firstName + " " + supportUser.user.lastName,
          jobTitle: supportUser?.title,
          userExternalId: supportUser.user.externalId,
          role: "",
          status: true,
          module: "",
          service: "",
          dateAdded: new Date(),
        };
        onEmployeeClick(supUser);
      });
    });
  };

  const onEmployeeClick = useCallback((employee: LazoTeamOfCompany) => {
    const [fname, lname] = employee.fullName.split(" ");
    const u: User = {
      externalId: employee.userExternalId,
      firstName: fname,
      lastName: lname,
    } as User;

    LazoChatConversationService.findConversationByMembers(
      [employee.userExternalId, currentUserExternalId],
      tenantMembership.tenant.externalId
    ).then((r) => {
      r.onSuccess((l) => {
        onChatSelect(mapToShell(l));
      });

      r.onError(() => {
        handleStartChat();
        setNewConversationMembers([employee.userExternalId]);
        setChatPreview({
          members: [{ user: u, isOwner: false } as ConversationMember],
          messages: [generateSingleFirstChatMessage(u)],
        } as LazoChatConversation);
      });
    });
  }, []);

  async function onCreateGroupChat(selectedUsers: string[]) {
    return createPrivateGroupChat(null, selectedUsers);
  }

  return (
    <ChatListContainer isPortal={false}>
      <ToolBar>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0.5rem",
            height: "2rem",
          }}
        >
          <Typography
            sx={{
              fontWeight: "500",
              fontSize: "1.125rem",
              lineHeight: "1.3438rem",
            }}
          >
            {t("inbox.title")}
          </Typography>

          <DrawerHeaderActionsContainer>
            <FilterMenu
              selectedActivityFilter={selectedActivityFilter}
              selectedDateFilter={selectedDateFilter}
              setActivityFilter={(f) => setSelectedActivityFilter(f)}
              setDateFilter={(f) => setSelectedDateFilter(f)}
            />
          </DrawerHeaderActionsContainer>
        </Box>

        <SearchField
          handleSearch={(e) => handleSearchField(e)}
          displaySearchButton={false}
          triggerSearchOnInputChange
          debouncer={debouncer}
          sx={{
            width: "100%",
            "& .MuiInputBase-root": {
              height: "2.5rem",
              width: "100%",
              margin: "0.5rem 0",
            },
            "& .MuiOutlinedInput-input": {
              padding: "10px",
            },
          }}
        />
      </ToolBar>

      <StyledDivider />

      {/* Chats Section - 50% */}
      <ChatsScrollContainer>
        {chatsFirstLoad && (
          <Box
            sx={{
              display: "flex",
              height: "100%",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LoadingDotsAnimated />
          </Box>
        )}
        {!chatsFirstLoad && (
          <Box sx={{ position: "relative", height: "100%" }}>
            <Box sx={{ overflowY: "auto", height: "100%" }}>
              {chats.map((chat, index) =>
                index !== chats.length - 1 ? (
                  <Box key={index}>
                    <ChatListItem
                      isSelected={selectedChat?.externalId === chat.externalId}
                      onChatSelect={() => onChatSelect(chat)}
                      chat={chat}
                      userTyping={userTyping && chat.externalId === userTyping.conversation?.externalId ? userTyping : undefined}
                    />
                    <StyledDivider />
                  </Box>
                ) : (
                  <Box key={index} ref={lastChatListItemRef}>
                    <ChatListItem
                      isSelected={selectedChat?.externalId === chat.externalId}
                      onChatSelect={() => onChatSelect(chat)}
                      chat={chat}
                      userTyping={userTyping && chat.externalId === userTyping.conversation?.externalId ? userTyping : undefined}
                    />
                    <StyledDivider />
                  </Box>
                )
              )}
            </Box>
            <LoadingIcon loading={chatsLoadingPage} />
          </Box>
        )}
        {!chatsFirstLoad && !chatsLoadingPage && chats.length === 0 && <EmptyChatList text={t("chatList.noChatsFound")} />}
      </ChatsScrollContainer>

      {/* Experts Section - 50%. Hidden in read-only mode (embedded history) so no new chats can start. */}
      {!readOnly && (
        <ExpertsScrollContainer>
          <LazoExpertsList onEmployeeClick={onEmployeeClick} onCreateGroupChat={onCreateGroupChat} onClickSupportChat={supportChat} />
        </ExpertsScrollContainer>
      )}
    </ChatListContainer>
  );
}

const ChatListContainer = styled(Box)<{ isPortal: boolean }>(({ isPortal }) => ({
  height: "100%",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  padding: isPortal ? "0" : "0",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  "-ms-overflow-style": "none",
}));

const ChatsScrollContainer = styled(Box)(() => ({
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
}));

const ExpertsScrollContainer = styled(Box)(() => ({
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  borderTop: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  "&::-webkit-scrollbar": {
    width: "0.4rem",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
}));

const StyledDivider = styled(Divider)(() => ({
  width: "100%",
}));

const ToolBar = styled(Box)(({ theme }) => ({
  display: "flex",
  position: "sticky",
  top: 0,
  backgroundColor: "#fff",
  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  zIndex: theme.zIndex.appBar,
  flexDirection: "column",
  alignItems: "stretch",
  justifyContent: "space-evenly",
  padding: ".75rem 1.5rem",
}));

const DrawerHeaderActionsContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "0.75rem",
}));
